import type { ApprovalChatEntry, RosterAgent, ServerEvent } from "@ark-bot/protocol";
import { useCallback, useEffect, useRef, useState } from "react";

import { useBackClose } from "../back.js";
import { LOCALES, setLocale, useLocale, useText, type Locale } from "../i18n.js";
import { PRACTICE_AVATAR, PRACTICE_ID, PRACTICE_NAME, PRACTICE_TOPICS, PracticeScript, botIdeas, chiefExample, chiefOfStaff, delegation, hiringManager, hiringMessage, ownTopic, pitchFrom, practiceTitle, stamp, suggestionFor, type Beat, type BotIdea, type PracticeTopic } from "../onboarding.js";
import { agentStatus, applyEvent, emptyChat, type ChatState } from "../state.js";
import { TOUR } from "../text/tour.js";

import { Avatar } from "./Avatar.js";
import { BrandMark } from "./BrandMark.js";
import { Chat, type AvatarFor } from "./Chat.js";
import { Composer } from "./Composer.js";
import { Transcript } from "./Transcript.js";

type Step = "language" | "welcome" | "chat" | "check" | "behind" | "chief" | "hire";
/**
 * Every step, in order: the language first, so everything after is read in
 * it. A lite host has no Full conversation to show, so it skips "behind"; a
 * workspace without its Chief of Staff and Hiring Manager has nobody to
 * delegate to, so it skips "chief".
 */
const STEPS: readonly Step[] = ["language", "welcome", "chat", "check", "behind", "chief", "hire"];

/** One thing in each language: the practice is played in both at once (usePractice). */
interface Both<T> {
  readonly en: T;
  readonly id: T;
}

/** The same step of the practice, written by each language's script. */
function both<T>(scripts: Both<PracticeScript>, make: (script: PracticeScript) => T): Both<T> {
  return { en: make(scripts.en), id: make(scripts.id) };
}

/** A line in each language, in that language, under its name on the first step. */
const GREETING: Record<Locale, string> = {
  id: "Halo! Bot kamu siap bantu kapan saja.",
  en: "Hi! Your bots are ready when you are.",
};

interface Props {
  /**
   * The host has a desktop (the full tier). A lite host's bots have a
   * terminal, files and the web, no screen to show and no Full conversation,
   * so the tour neither promises those nor walks through them.
   */
  readonly screen: boolean;
  /** The real roster, for the bots already on the team. */
  readonly agents: readonly RosterAgent[];
  /** Opens the Bot Hiring Manager's chat and sends it the user's first message: the job their first bot is for. */
  readonly onHire: (managerId: string, message: string) => Promise<void>;
  /** The tour is over, taken or skipped; either way it does not open by itself again. */
  readonly onClose: () => void;
  readonly closing?: boolean;
}

/** Folds scripted events into the practice chat, numbered the way the host numbers its log. */
function applyAll(chat: ChatState, events: readonly { readonly seq: number; readonly event: ServerEvent }[]): ChatState {
  return events.reduce((state, { seq, event }) => applyEvent(state, event, seq), chat);
}

interface Practice {
  /** The chat as each language's script played it; the tour shows the one the app is in. */
  readonly chats: Both<ChatState>;
  readonly scripts: Both<PracticeScript>;
  /** Plays the same beats, one language's words each, into both chats together. */
  readonly play: (beats: Both<readonly Beat[]>, onDone?: () => void) => void;
}

/**
 * The practice chat: the scripted turns played into the same reducer a real
 * chat folds the host's events into, one beat after another. It is played in
 * both languages at once, the same turns and timings in each one's words, so
 * a language picked mid-tour shows everything already said in it too. Every
 * timer is dropped when the tour goes.
 */
function usePractice(opening: (script: PracticeScript) => readonly Beat[]): Practice {
  const [scripts] = useState<Both<PracticeScript>>(() => ({ en: new PracticeScript("en"), id: new PracticeScript("id") }));
  const seq = useRef(0);
  // Numbered alike in both languages: each is the same turn in other words.
  const number = (events: Both<readonly ServerEvent[]>): Both<{ seq: number; event: ServerEvent }[]> => {
    const start = seq.current;
    seq.current += events.en.length;
    const at = Date.now();
    const numbered = (list: readonly ServerEvent[]): { seq: number; event: ServerEvent }[] => list.map((event, index) => ({ seq: start + index + 1, event: stamp(event, at) }));
    return { en: numbered(events.en), id: numbered(events.id) };
  };
  // What the chat opens with is there from the first paint, as a new bot's hello is.
  const [chats, setChats] = useState<Both<ChatState>>(() => {
    const opened = number({ en: opening(scripts.en).flatMap((beat) => beat.events), id: opening(scripts.id).flatMap((beat) => beat.events) });
    return { en: applyAll(emptyChat, opened.en), id: applyAll(emptyChat, opened.id) };
  });
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending) clearTimeout(timer);
      pending.clear();
    };
  }, []);
  const play = useCallback((beats: Both<readonly Beat[]>, onDone?: () => void): void => {
    let at = 0;
    // The scripts write the same beats with the same waits; only the words differ.
    beats.en.forEach((beat, index) => {
      at += beat.wait;
      const timer = setTimeout(() => {
        timers.current.delete(timer);
        const numbered = number({ en: beat.events, id: beats.id[index]?.events ?? beat.events });
        setChats((current) => ({ en: applyAll(current.en, numbered.en), id: applyAll(current.id, numbered.id) }));
        if (index === beats.en.length - 1) onDone?.();
      }, at);
      timers.current.add(timer);
    });
    if (beats.en.length === 0) onDone?.();
    // `number` reads only a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { chats, scripts, play };
}

/**
 * The first step: each language by its own name, with a line in it, so
 * someone who cannot read the one on screen still finds theirs. Picking one
 * changes the page at once.
 */
function LanguageChoice(): React.JSX.Element {
  const t = useText(TOUR);
  const current = useLocale();
  return (
    <div className="tour-languages" role="radiogroup" aria-label={t.languageSwitch}>
      {LOCALES.map((one) => (
        <button key={one.id} type="button" role="radio" aria-checked={current === one.id} lang={one.id} className={`tour-language${current === one.id ? " chosen" : ""}`} onClick={() => setLocale(one.id)}>
          <strong>{one.name}</strong>
          <span className="muted">{GREETING[one.id]}</span>
        </button>
      ))}
    </div>
  );
}

/** The language, switchable on every step after the first: a short code for each, its name on hover. */
function LanguageSwitch(): React.JSX.Element {
  const t = useText(TOUR);
  const current = useLocale();
  return (
    <div className="tour-language-switch" role="group" aria-label={t.languageSwitch}>
      {LOCALES.map((one) => (
        <button key={one.id} type="button" lang={one.id} aria-pressed={current === one.id} title={one.name} aria-label={one.name} onClick={() => setLocale(one.id)}>
          {one.id.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

const PaneIcon = (): React.JSX.Element => (
  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
    <rect x="2" y="3.5" width="16" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path d="M7 17.5h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/** The first screen's picture: a few bots at work, each with its job and what it is doing. */
function Crew(): React.JSX.Element {
  const t = useText(TOUR);
  const ideas = botIdeas();
  const crew: readonly { readonly idea: BotIdea; readonly status: string; readonly tone: "live" | "attention" | "quiet" }[] = [
    { idea: ideas[0]!, status: t.crewSearching, tone: "live" },
    { idea: ideas[1]!, status: t.crewWaiting, tone: "attention" },
    { idea: ideas[3]!, status: t.crewWatching, tone: "quiet" },
  ];
  return (
    <div className="tour-crew" aria-hidden="true">
      {crew.map(({ idea, status, tone }, index) => (
        <div key={idea.name} className={`tour-crew-card tone-${tone}`} style={{ animationDelay: `${index * 120}ms` }}>
          <Avatar id={`crew-${idea.name}`} name={idea.name} avatar={idea.avatar} size={40} />
          <span className="tour-crew-body">
            <span className="tour-crew-line">
              <strong>{idea.name}</strong>
              <span className="tag">{idea.title}</span>
            </span>
            <span className="tour-crew-status">{status}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * The practice bot's side pane, laid out as a real one: its screen or its
 * face, its routines and skills, and on the full tier the full conversation
 * one tap away. A lite host has neither the screen nor the conversation.
 */
function PracticePane({ screen, chat, mode, onMode, onClose }: { readonly screen: boolean; readonly chat: ChatState; readonly mode: "details" | "conversation"; readonly onMode: (mode: "details" | "conversation") => void; readonly onClose: () => void }): React.JSX.Element {
  const t = useText(TOUR);
  return (
    <aside className={`details-pane tour-pane mode-${mode}`} aria-label={mode === "conversation" ? t.fullConversation : t.details}>
      <div className="details-head">
        {mode === "details" ? null : (
          <button type="button" className="icon" onClick={() => onMode("details")} aria-label={t.backToDetails} title={t.back}>
            ‹
          </button>
        )}
        <span className="details-title">{mode === "conversation" ? t.fullConversation : t.details}</span>
        <button type="button" className="icon" onClick={onClose} aria-label={t.hideDetails} title={t.hidePane}>
          »
        </button>
      </div>
      {mode === "conversation" ? (
        <Transcript items={chat.items} busy={chat.busy} />
      ) : (
        <div className="details-body">
          {screen ? (
            <section className="details-section screen-section">
              <div className="screen-card tour-screen" aria-hidden="true">
                <span className="tour-screen-window">
                  <i />
                  <i />
                  <i />
                </span>
                <span className="tour-screen-pointer" />
              </div>
              <div className="screen-caption muted">{t.screenCaption(PRACTICE_NAME)}</div>
            </section>
          ) : (
            <section className="details-section group-head">
              <Avatar id={PRACTICE_ID} name={PRACTICE_NAME} avatar={PRACTICE_AVATAR} size={72} />
              <h2 className="group-name">{PRACTICE_NAME}</h2>
              <p className="group-description muted">{t.practiceAbout}</p>
            </section>
          )}
          <section className="details-section">
            <div className="details-section-head">
              <h3>{t.routines}</h3>
            </div>
            <ul className="summary-list">
              <li>
                <strong>{t.morningBrief}</strong>
                <span className="muted">{t.morningBriefWhen}</span>
              </li>
            </ul>
            <p className="muted">{t.routinesAbout}</p>
          </section>
          <section className="details-section">
            <div className="details-section-head">
              <h3>{t.skills}</h3>
            </div>
            <p className="muted">{t.skillsAbout}</p>
          </section>
          {screen ? (
            <section className="details-section">
              <div className="details-section-head">
                <h3>{t.fullConversation}</h3>
                <button type="button" className="linkish tour-pulse" onClick={() => onMode("conversation")}>
                  {t.show}
                </button>
              </div>
              <p className="muted">{t.conversationAbout}</p>
            </section>
          ) : null}
        </div>
      )}
    </aside>
  );
}

/**
 * The first real bot, hired rather than filled in: the user says what it is
 * for, and the Bot Hiring Manager gets that as their first message, asks its
 * few questions in its own chat and makes the bot. The preview shows the
 * message as it will land, so nothing is sent that the user has not seen.
 */
function Hire({ manager, task, onHire, onDone }: { readonly manager: RosterAgent; readonly task: string | null; readonly onHire: Props["onHire"]; readonly onDone: () => void }): React.JSX.Element {
  const t = useText(TOUR);
  const ideas = botIdeas();
  // What the user typed; until they type, the job is written for them (from their practice task, or the idea they picked) in the app's language, so switching it rewrites that too.
  const [typed, setTyped] = useState<string | null>(null);
  const [picked, setPicked] = useState<number | null>(null);
  const job = typed ?? (picked != null ? (ideas[picked]?.pitch ?? "") : pitchFrom(task));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const role = manager.config.title?.trim() ?? "";
  // Nothing written yet: the field and the ideas glow; once there is a job, the send button does.
  const empty = job.trim().length === 0;

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (sending || empty) return;
    setSending(true);
    setError(null);
    try {
      await onHire(manager.id, hiringMessage(job));
      onDone();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
      setSending(false);
    }
  };

  return (
    <div className="tour-hire">
      <div className="tour-hire-chat" aria-label={t.hireChat(manager.name)}>
        <header className="tour-hire-head">
          <Avatar id={manager.id} name={manager.name} avatar={manager.config.avatar} size={34} />
          <span className="title-line">
            <strong>{manager.name}</strong>
            {role.length > 0 && role !== manager.name ? <span className="tag">{role}</span> : null}
          </span>
        </header>
        <div className="tour-hire-body">
          {job.trim().length > 0 ? (
            <div className="bubble-row user">
              <div className="bubble user">{hiringMessage(job)}</div>
            </div>
          ) : (
            <p className="muted tour-hire-empty">{t.hireEmpty}</p>
          )}
          <p className="muted tour-hire-next">{t.hireNext}</p>
        </div>
      </div>
      <form className="agent-settings profile-form tour-form" onSubmit={(event) => void submit(event)}>
        <label>
          {t.hireQuestion}
          <textarea
            rows={3}
            autoFocus
            className={empty ? "tour-glow" : undefined}
            value={job}
            onChange={(event) => {
              setTyped(event.target.value);
              setPicked(null);
            }}
            placeholder={t.hirePlaceholder}
          />
        </label>
        <div className="tour-ideas" role="group" aria-label={t.hireIdeas}>
          {ideas.map((idea, at) => (
            <button
              key={at}
              type="button"
              className={`tour-idea${empty ? " tour-pulse" : ""}`}
              aria-pressed={picked === at}
              onClick={() => {
                setTyped(null);
                setPicked(at);
              }}
            >
              <Avatar id={`idea-${idea.name}`} name={idea.name} avatar={idea.avatar} size={22} />
              <span>{idea.name}</span>
            </button>
          ))}
        </div>
        {error != null ? <div className="settings-error">{error}</div> : null}
        <div className="settings-actions">
          <button type="submit" className={`primary${empty || sending ? "" : " tour-pulse"}`} disabled={sending || empty}>
            {sending ? t.sending : t.sendTo(manager.name)}
          </button>
          <button type="button" className="quiet" onClick={onDone} disabled={sending}>
            {t.lookAround}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Who a practice chat is with: the practice bot, or the user's own Chief of Staff played by the script. */
interface PracticeBot {
  readonly id: string;
  readonly name: string;
  readonly title: string;
  readonly avatar: RosterAgent["config"]["avatar"];
}

/**
 * One practice chat, laid out as the app's own: the header with the bot's
 * face and what it is doing, the chat, suggestions until something is sent,
 * the composer, and optionally the side pane. The chat is the real
 * component; only the events behind it are scripted.
 */
function PracticeWindow({ bot, chat, avatarFor, suggestions, onSend, onResolve, pane }: {
  readonly bot: PracticeBot;
  readonly chat: ChatState;
  readonly avatarFor: AvatarFor;
  /** Offered over the composer; null once something has been sent. */
  readonly suggestions: readonly string[] | null;
  readonly onSend: (text: string) => Promise<void>;
  readonly onResolve?: (approvalId: string, approved: boolean) => Promise<void>;
  /** The side pane's button and the pane itself, where this chat has one. */
  readonly pane?: { readonly open: boolean; readonly pulse: boolean; readonly title: string; readonly onOpen: () => void; readonly content: React.ReactNode };
}): React.JSX.Element {
  const t = useText(TOUR);
  const status = agentStatus(chat);
  const headline = status.awaitingApproval ? t.awaitingApproval : status.composing ? t.typing : status.activity != null ? `${status.activity[0]!.toUpperCase()}${status.activity.slice(1)}…` : status.working ? t.working : t.practice;
  // What the chat asks of the user right now glows: the suggestions and the message box until something is sent, then a card while it waits.
  const cue = suggestions != null ? " cue-compose" : status.awaitingApproval ? " cue-approve" : "";
  return (
    <div className={`tour-window${pane?.open === true ? " with-details" : ""}${cue}`}>
      {/* As the app lays a chat out: its own column, with the side pane beside it from the top down. */}
      <div className="chat-column">
        <header className="topbar">
          {avatarFor(bot.id, bot.name, 34)}
          <div className="title">
            <span className="title-line">
              <h1>{bot.name}</h1>
              {bot.title.length > 0 && bot.title !== bot.name ? <span className="tag">{bot.title}</span> : null}
            </span>
            <span className={`subtitle${status.awaitingApproval ? " attention" : status.working ? " live" : ""}`}>{headline}</span>
          </div>
          {pane == null || pane.open ? null : (
            <button type="button" className={`icon${pane.pulse ? " tour-pulse" : ""}`} onClick={pane.onOpen} aria-label={t.showPane} title={pane.title}>
              <PaneIcon />
            </button>
          )}
        </header>
        <div className="chat-pane">
          <Chat
            messages={chat.messages}
            status={status}
            agentId={bot.id}
            agentName={bot.name}
            avatarFor={avatarFor}
            mentionables={[]}
            onOpenAgent={() => undefined}
            hasMore={false}
            loadingMore={false}
            onLoadMore={() => undefined}
            onSubmitForm={async () => undefined}
            onCancelForm={async () => undefined}
            onResolve={onResolve ?? (async () => undefined)}
            onResolveHandoff={async () => undefined}
            onTakeOver={() => undefined}
            onAnswerContact={async () => undefined}
          />
          {suggestions != null ? (
            <div className="tour-suggestions" role="group" aria-label={t.suggestions}>
              {suggestions.map((one) => (
                <button key={one} type="button" className="tour-suggestion" onClick={() => void onSend(one)}>
                  {one}
                </button>
              ))}
            </div>
          ) : null}
          <Composer agentName={bot.name} mentionables={[]} avatarFor={avatarFor} onSend={(text) => onSend(text)} acceptsPictures={false} />
        </div>
      </div>
      {pane?.open === true ? pane.content : null}
    </div>
  );
}

/** "your Chief of Staff" for a bot still called by its role; its own name once the user has given it one. */
function called(agent: RosterAgent, role: RegExp, t: typeof TOUR.en): string {
  return role.test(agent.name) ? t.yourBot(agent.name) : agent.name;
}

/**
 * The first-run tour, over the whole page: what this is, then a practice run
 * the user does themselves in a chat that is the real one, driven by a
 * script instead of the host. They send a message and watch a bot work,
 * answer an approval card, open the side pane to see everything it did (the
 * full tier only), watch their Chief of Staff delegate a request, and
 * finally hire their first real bot by telling the Bot Hiring Manager what
 * it is for. Nothing before that last step reaches the host. The prototype
 * opens its own onboarding on a new account; this one teaches by doing
 * rather than by showing.
 */
export function Tour({ screen, agents, onHire, onClose, closing = false }: Props): React.JSX.Element {
  const t = useText(TOUR);
  const language = useLocale();
  const [step, setStep] = useState<Step>("language");
  const scout = usePractice((script) => script.hello());
  const chat = scout.chats[language];
  const play = scout.play;
  // The Chief of Staff's lesson has a chat of its own; an on-demand bot says nothing until it is asked.
  const chiefPractice = usePractice(() => []);
  const chiefChat = chiefPractice.chats[language];
  const [topic, setTopic] = useState<PracticeTopic | null>(null);
  const [taskDone, setTaskDone] = useState(false);
  const offer = useRef<{ readonly turnId: string; readonly approvalId: string; readonly callId: string } | null>(null);
  const settled = useRef(false);
  const [answered, setAnswered] = useState(false);
  const [paneOpen, setPaneOpen] = useState(false);
  const [paneMode, setPaneMode] = useState<"details" | "conversation">("details");
  const [seenConversation, setSeenConversation] = useState(false);
  const [delegated, setDelegated] = useState<string | null>(null);
  const [chiefDone, setChiefDone] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const status = agentStatus(chat);

  // Who the last two steps are about; a workspace without them skips what it cannot show.
  const manager = hiringManager(agents);
  const chief = chiefOfStaff(agents);
  const steps = STEPS.filter((one) => (one !== "behind" || screen) && (one !== "chief" || (chief != null && manager != null)));
  const index = steps.indexOf(step);

  // Back, on a phone, leaves the tour the way it closes any page laid over the app.
  useBackClose(!closing, onClose);
  // Each step is read from its heading, so a screen reader starts at the new words.
  useEffect(() => heading.current?.focus(), [step]);

  /** Faces by id: the practice bot's own, and the real one for anyone on the user's team. */
  const avatarFor: AvatarFor = (id, name, size) => {
    const known = agents.find((agent) => agent.id === id);
    return <Avatar id={id} name={known?.name ?? name} avatar={id === PRACTICE_ID ? PRACTICE_AVATAR : known?.config.avatar} {...(size == null ? {} : { size })} />;
  };

  const pendingCard = chat.messages.find((entry): entry is ApprovalChatEntry => entry.kind === "approval" && entry.status === "pending") ?? null;

  /** The card answered, once: a second press before the first lands would play the answer twice. */
  const settle = (card: ApprovalChatEntry, approved: boolean): void => {
    const made = offer.current;
    if (settled.current || made == null || card.approvalId !== made.approvalId) return;
    settled.current = true;
    // Each language's chat has its own copy of the card, in its words.
    const cardIn = (script: PracticeScript): ApprovalChatEntry => scout.chats[script.language ?? language].messages.find((entry): entry is ApprovalChatEntry => entry.kind === "approval" && entry.approvalId === card.approvalId) ?? card;
    play(both(scout.scripts, (script) => script.answer(cardIn(script), made.callId, made.turnId, approved)), () => setAnswered(true));
  };

  const send = async (text: string): Promise<void> => {
    if (topic == null) {
      const chosen = suggestionFor(text) ?? ownTopic(text);
      setTopic(chosen);
      play(both(scout.scripts, (script) => script.task(chosen)), () => setTaskDone(true));
      return;
    }
    // As in a real chat, a new message while a card waits is taken as a no: the card goes, and the bot says so.
    if (pendingCard != null) {
      play(both(scout.scripts, (script) => script.user(text).beats));
      settle(pendingCard, false);
      return;
    }
    play(both(scout.scripts, (script) => script.aside(text, chat.busy)));
  };

  /** The Chief of Staff's chat: the first request is delegated to the hiring manager; anything after is taken on board. */
  const sendToChief = async (text: string): Promise<void> => {
    if (manager == null) return;
    if (delegated == null) {
      setDelegated(text);
      chiefPractice.play(both(chiefPractice.scripts, (script) => script.delegate(delegation(text, manager.name, script.language), { id: manager.id, name: manager.name })), () => setChiefDone(true));
      return;
    }
    chiefPractice.play(both(chiefPractice.scripts, (script) => script.aside(text, chiefChat.busy)));
  };

  const go = (next: Step): void => {
    // The bot carries on by itself into the next lesson: it offers to send the result, and the send waits on a card.
    if (next === "check" && offer.current == null && topic != null) {
      const made = both(scout.scripts, (script) => script.offer(topic));
      // Both scripts number alike, so either one's ids are the card's.
      offer.current = made.en;
      play({ en: made.en.beats, id: made.id.beats });
    }
    setStep(next);
  };

  const resolve = async (approvalId: string, approved: boolean): Promise<void> => {
    if (pendingCard?.approvalId === approvalId) settle(pendingCard, approved);
  };

  const changeMode = (mode: "details" | "conversation"): void => {
    setPaneMode(mode);
    if (mode === "conversation") setSeenConversation(true);
  };

  const onTeam = agents.filter((agent) => agent.kind === "agent").slice(0, 3);
  const ready = step === "chat" ? taskDone : step === "check" ? answered : step === "behind" ? seenConversation : step === "chief" ? chiefDone : true;

  const coach = ((): { readonly title: string; readonly body: React.ReactNode; readonly hint?: string } => {
    switch (step) {
      case "language":
        return { title: t.languageTitle, body: <p>{t.languageAbout}</p> };
      case "welcome":
        return {
          title: t.welcomeTitle,
          body: (
            <>
              <p>{t.welcomeTeam}</p>
              <p>{screen ? t.welcomeComputer : t.welcomeWorkspace}</p>
              <p>{t.welcomePractice}</p>
            </>
          ),
        };
      case "chat":
        return {
          title: t.chatTitle,
          body:
            topic == null ? (
              <p>{t.chatAsk(PRACTICE_NAME)}</p>
            ) : !taskDone ? (
              <>
                <p>{t.chatWorking(PRACTICE_NAME)}</p>
                <p>{t.chatNoWait}</p>
              </>
            ) : (
              <p>{t.chatDone}</p>
            ),
          ...(taskDone ? {} : { hint: topic == null ? t.chatHintSend : t.chatHintWatch }),
        };
      case "check":
        return {
          title: t.checkTitle,
          body: answered ? <p>{t.checkDone}</p> : <p>{t.checkAbout}</p>,
          ...(answered ? {} : { hint: status.awaitingApproval ? t.checkHintAnswer : t.checkHintComing }),
        };
      case "behind":
        return {
          title: t.behindTitle,
          body: seenConversation ? <p>{t.behindSeen}</p> : <p>{t.behindAbout}</p>,
          ...(seenConversation ? {} : { hint: paneOpen ? t.behindHintShow : t.behindHintOpen }),
        };
      case "chief": {
        const who = chief == null ? t.chiefDefault : called(chief, /\bchief of staff\b/i, t);
        return {
          title: t.chiefTitle(who),
          body:
            delegated == null ? (
              <>
                <p>{t.chiefAbout(who)}</p>
                <p className="muted">{t.chiefPracticeToo}</p>
              </>
            ) : !chiefDone ? (
              <p>{t.chiefWatching(manager?.name ?? "Bot Hiring Manager")}</p>
            ) : (
              <p>{t.chiefDone(who)}</p>
            ),
          ...(chiefDone ? {} : { hint: delegated == null ? t.chiefHintExample : t.chiefHintWatch }),
        };
      }
      case "hire":
        return manager == null
          ? {
              title: t.makeTitle,
              body: <p>{t.makeAbout}</p>,
            }
          : {
              title: t.hireTitle,
              body: (
                <>
                  <p>{t.hireAbout(called(manager, /\bhiring manager\b/i, t))}</p>
                  <p>{t.hireHow}</p>
                  {onTeam.length > 1 ? (
                    <div className="tour-team">
                      <span className="tour-team-label">{t.onTeam}</span>
                      {onTeam.map((agent) => (
                        <span key={agent.id} className="tour-team-row">
                          <Avatar id={agent.id} name={agent.name} avatar={agent.config.avatar} size={24} />
                          <span>
                            <strong>{agent.name}</strong>
                            {agent.config.title != null && agent.config.title.trim().length > 0 && agent.config.title.trim() !== agent.name ? <span className="muted"> · {agent.config.title}</span> : null}
                          </span>
                        </span>
                      ))}
                    </div>
                  ) : null}
                </>
              ),
            };
    }
  })();

  const practice = step === "chat" || step === "check" || step === "behind";

  return (
    <div className={`tour${closing ? " closing" : ""}`} role="dialog" aria-modal="true" aria-labelledby="tour-title">
      <section className={`tour-coach step-${step}`}>
        <div className="tour-coach-head">
          <BrandMark />
          <span className="tour-brand">ark-bot</span>
          {step === "language" ? null : <LanguageSwitch />}
          <button type="button" className="quiet tour-skip" onClick={onClose}>
            {step === "language" || step === "welcome" ? t.skip : t.skipTour}
          </button>
        </div>
        <ol className="tour-progress" aria-label={t.progress(index + 1, steps.length)}>
          {steps.map((one, at) => (
            <li key={one} className={at < index ? "done" : at === index ? "current" : ""} />
          ))}
        </ol>
        {/* Keyed on the step, so each new lesson plays its way in. */}
        <h2 key={step} id="tour-title" ref={heading} tabIndex={-1}>
          {coach.title}
        </h2>
        <div className="tour-body" aria-live="polite">
          {coach.body}
        </div>
      </section>

      <section className={`tour-stage stage-${step}`} aria-label={practice ? t.stagePractice(PRACTICE_NAME) : step === "chief" && chief != null ? t.stagePractice(chief.name) : step === "hire" ? t.stageHire : step === "language" ? t.stageLanguage : t.stageWelcome}>
        {step === "language" ? <LanguageChoice /> : null}
        {step === "welcome" || (step === "hire" && manager == null) ? <Crew /> : null}
        {practice ? (
          <PracticeWindow
            key="scout"
            bot={{ id: PRACTICE_ID, name: PRACTICE_NAME, title: practiceTitle(), avatar: PRACTICE_AVATAR }}
            chat={chat}
            avatarFor={avatarFor}
            suggestions={topic == null ? PRACTICE_TOPICS.map((one) => one.ask) : null}
            onSend={send}
            onResolve={resolve}
            pane={{
              open: paneOpen,
              pulse: step === "behind",
              title: screen ? t.paneTitleScreen : t.paneTitle,
              onOpen: () => {
                setPaneOpen(true);
                setPaneMode("details");
              },
              content: <PracticePane screen={screen} chat={chat} mode={paneMode} onMode={changeMode} onClose={() => setPaneOpen(false)} />,
            }}
          />
        ) : null}
        {step === "chief" && chief != null ? (
          <PracticeWindow
            key="chief"
            bot={{ id: chief.id, name: chief.name, title: chief.config.title?.trim() ?? "", avatar: chief.config.avatar }}
            chat={chiefChat}
            avatarFor={avatarFor}
            suggestions={delegated == null ? [chiefExample()] : null}
            onSend={sendToChief}
          />
        ) : null}
        {step === "hire" && manager != null ? <Hire manager={manager} task={topic?.ask ?? null} onHire={onHire} onDone={onClose} /> : null}
        {/* What to do now and the way on, under the practice where the user is already looking, not back at the coach's edge. */}
        {step === "hire" && manager != null ? null : (
          <div className="tour-next">
            {coach.hint != null ? <p className="tour-hint">{coach.hint}</p> : null}
            <div className="tour-actions">
              {/* Back to the language from the welcome, and between the lessons; not from the practice back to the welcome. */}
              {step === "welcome" || index > 2 ? (
                <button type="button" className="quiet" onClick={() => setStep(steps[index - 1]!)}>
                  {t.back}
                </button>
              ) : null}
              {step !== "hire" ? (
                <button type="button" className={`primary${ready ? " tour-pulse" : ""}`} disabled={!ready} onClick={() => go(steps[index + 1]!)}>
                  {step === "language" ? t.languageNext : step === "welcome" ? t.tryIt : t.next}
                </button>
              ) : (
                <button type="button" className="primary tour-pulse" onClick={onClose}>
                  {t.finish}
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
